import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Filter, Trash2, ChevronLeft, ChevronRight, X } from 'lucide-react'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { services } from '@/lib/api'
import CdnImage from '@/components/shared/CdnImage'
import Loading from '@/components/shared/Loading'
import { DEFAULT_AVATAR } from '@/constants'
import { Teacher } from '@/interface/types/user'
import { TeacherForm, type TeacherFormValues } from './TeacherForm'
import { useToast } from '@/components/ui/use-toast'
import type { CreateTeacherDTO, UpdateTeacherDTO } from '@/interface/types/user'
import { BulkTeacherImport } from './BulkTeacherImport'
import { useTranslation } from 'react-i18next'

interface TeacherFilters {
  cin: string
  name: string
  title: string
}

interface ActiveFilter {
  key: keyof TeacherFilters
  value: string
  label: string
}

export default function TeacherManagement() {
  const { t } = useTranslation('admin')
  const [selectedTeacher, setSelectedTeacher] = useState<Teacher | null>(null)
  const [isProfileModalOpen, setIsProfileModalOpen] = useState<boolean>(false)
  const [isAddTeacherOpen, setIsAddTeacherOpen] = useState<boolean>(false)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState<boolean>(false)
  const [teacherToDelete, setTeacherToDelete] = useState<Teacher | null>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [activeFilters, setActiveFilters] = useState<ActiveFilter[]>([])
  const [searchQuery, setSearchQuery] = useState('')
  const [showFilters, setShowFilters] = useState(false)
  const [showLoadingForEmpty, setShowLoadingForEmpty] = useState(false)

  const {
    data: teachers = [],
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ['teachers'],
    queryFn: services.teacher.getAll,
    staleTime: 1000 * 60 * 5,
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    refetchOnWindowFocus: true,
  })

  useEffect(() => {
    if (!isLoading && (!teachers || teachers.length === 0)) {
      setShowLoadingForEmpty(true)
      const timer = setTimeout(() => {
        setShowLoadingForEmpty(false)
      }, 1000)
      return () => clearTimeout(timer)
    } else {
      setShowLoadingForEmpty(false)
    }
  }, [isLoading, teachers])

  const { 
    data: subjects = [], 
    isLoading: isLoadingSubjects,
    refetch: refetchSubjects 
  } = useQuery({
    queryKey: ['subjects'],
    queryFn: async () => {
      try {
        const apiUrl = import.meta.env.VITE_API_URL;
        
        try {
          const response = await fetch(`${apiUrl}/subject`, {
            headers: {
              'Content-Type': 'application/json',
            },
            credentials: 'include',
          });
          
          if (!response.ok) throw new Error(`API returned ${response.status}: ${response.statusText}`);
          const data = await response.json();
          return Array.isArray(data) ? data : [];
        } catch (error1) {
          console.warn('Error fetching from /subject endpoint:', error1);
          const response = await fetch(`${apiUrl}/subjects`, {
            headers: {
              'Content-Type': 'application/json',
            },
            credentials: 'include',
          });
          
          if (!response.ok) throw new Error(`API returned ${response.status}: ${response.statusText}`);
          const data = await response.json();
          return Array.isArray(data) ? data : [];
        }
      } catch (error) {
        // console.error('Error fetching subjects:', error);
        return [];
      }
    },
    staleTime: 1000 * 30,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
  })

  const queryClient = useQueryClient()
  const { toast } = useToast()

  const deleteTeacherMutation = useMutation({
    mutationFn: (id: string) => services.teacher.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teachers'] })
      setIsDeleteDialogOpen(false)
      setTeacherToDelete(null)
      toast({
        title: t('teacher-folder.teacherManagement.notifications.delete.title'),
        description: t('teacher-folder.teacherManagement.notifications.delete.description'),
        variant: "default",
      })
    },
    onError: () => {
      // console.error('Error deleting teacher:', error)
      toast({
        title: t('teacher-folder.teacherManagement.notifications.error.delete.title'),
        description: t('teacher-folder.teacherManagement.notifications.error.delete.description'),
        variant: "destructive",
      })
      setIsDeleteDialogOpen(false)
    },
  })

  const updateTeacherMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateTeacherDTO }) =>
      services.teacher.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teachers'] })
      setIsProfileModalOpen(false)
      toast({ 
        title: t('teacher-folder.teacherManagement.notifications.update.title'),
        description: t('teacher-folder.teacherManagement.notifications.update.description'),
        variant: "default" 
      })
    },
    onError: () => {
      // console.error('Failed to update teacher:', error)
      toast({ 
        title: t('teacher-folder.teacherManagement.notifications.error.update.title'),
        description: t('teacher-folder.teacherManagement.notifications.error.update.description'),
        variant: "destructive" 
      })
    }
  })

  const createTeacherMutation = useMutation({
    mutationFn: (data: CreateTeacherDTO) => services.teacher.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teachers'] })
      setIsAddTeacherOpen(false)
      toast({
        title: t('teacher-folder.teacherManagement.notifications.create.title'),
        description: t('teacher-folder.teacherManagement.notifications.create.description'),
        variant: "default",
      })
    },
    onError: (error: any) => {
      // console.error('Failed to create teacher:', error)
      
      let errorTitle = t('teacher-folder.teacherManagement.notifications.error.create.title') || 'Can\'t create new teacher'
      let errorDescription = ''
      
      // Better error handling for API responses
      if (error.response?.data) {
        const apiError = error.response.data
        let backendMessage = ''
        
        // Extract the backend error message
        if (typeof apiError.message === 'string') {
          backendMessage = apiError.message
        } else if (Array.isArray(apiError.message)) {
          // Handle array of error objects like [{"field":"cin", "message":"cin duplicated"}]
          const errorMessages = apiError.message.map((err: any) => {
            if (typeof err === 'string') return err
            if (err.field && err.message) {
              // Check if it's a CIN duplication error
              if (err.field === 'cin' && err.message.toLowerCase().includes('duplicated')) {
                return t('teacher-folder.teacherManagement.toast.errors.cinExists', 'cin is duplicated')
              }
              if (err.field === 'email' && err.message.toLowerCase().includes('duplicated')) {
                return t('teacher-folder.teacherManagement.toast.errors.emailExists', 'email is duplicated')
              }
              return err.message
            }
            return JSON.stringify(err)
          })
          backendMessage = errorMessages.join(', ')
        } else if (apiError.message && typeof apiError.message === 'object') {
          // Handle validation errors object
          const validationErrors = []
          
          if (apiError.message.email) {
            const emailError = apiError.message.email
            if (typeof emailError === 'string' && emailError.toLowerCase().includes('duplicated')) {
              validationErrors.push(t('teacher-folder.teacherManagement.toast.errors.emailExists', 'email is duplicated'))
            } else {
              validationErrors.push(emailError)
            }
          }
          if (apiError.message.cin) {
            // Check if it's a duplication error
            const cinError = apiError.message.cin
            if (typeof cinError === 'string' && cinError.toLowerCase().includes('duplicated')) {
              validationErrors.push(t('teacher-folder.teacherManagement.toast.errors.cinExists', 'cin is duplicated'))
            } else {
              validationErrors.push(cinError)
            }
          }
          if (apiError.message.birthday) {
            validationErrors.push(apiError.message.birthday)
          }
          
          backendMessage = validationErrors.join('. ')
        } else if (apiError.error === 'CONFLICT') {
          backendMessage = 'A teacher with this information already exists'
        } else if (apiError.error === 'BAD_REQUEST') {
          backendMessage = 'Invalid data provided'
        } else if (apiError.error) {
          backendMessage = apiError.error
        }
        
        // Detect if it's a CIN or email error and show simple message
        const lowerMessage = backendMessage.toLowerCase()
        if (lowerMessage.includes('cin') && (lowerMessage.includes('duplicated') || lowerMessage.includes('duplicate'))) {
          errorDescription = t('teacher-folder.teacherManagement.toast.errors.cinExists', 'cin is duplicated')
        } else if (lowerMessage.includes('email') && (lowerMessage.includes('duplicated') || lowerMessage.includes('duplicate'))) {
          errorDescription = t('teacher-folder.teacherManagement.toast.errors.emailExists', 'email is duplicated')
        } else {
          // Use backend message as is, but simplify if it's JSON
          errorDescription = backendMessage || t('teacher-folder.teacherManagement.notifications.error.create.description') || 'An error occurred while creating the teacher'
        }
      } else if (error.message) {
        // Check error.message for CIN/email keywords
        const lowerMessage = error.message.toLowerCase()
        if (lowerMessage.includes('cin') && (lowerMessage.includes('duplicated') || lowerMessage.includes('duplicate'))) {
          errorDescription = t('teacher-folder.teacherManagement.toast.errors.cinExists', 'cin is duplicated')
        } else if (lowerMessage.includes('email') && (lowerMessage.includes('duplicated') || lowerMessage.includes('duplicate'))) {
          errorDescription = t('teacher-folder.teacherManagement.toast.errors.emailExists', 'email is duplicated')
        } else {
          errorDescription = error.message
        }
      } else {
        errorDescription = t('teacher-folder.teacherManagement.notifications.error.create.description') || 'An error occurred while creating the teacher'
      }
      
      // Show error toast with proper styling
      toast({
        title: errorTitle,
        description: errorDescription,
        variant: "destructive",
      })
    },
  })

  // Calculate pagination values before early returns
  const validTeachers = Array.isArray(teachers) ? teachers.filter(teacher => 
    teacher && teacher.id && (teacher.firstname || teacher.lastname)
  ) : []

  const filteredTeachers = validTeachers.filter((teacher) => {
    const searchMatches =
      searchQuery === '' ||
      `${teacher.firstname} ${teacher.lastname} ${teacher.cin}`
        .toLowerCase()
        .includes(searchQuery.toLowerCase())

    const filtersMatch = activeFilters.every((filter) => {
      switch (filter.key) {
        case 'name':
          const fullName = `${teacher.firstname} ${teacher.lastname}`.toLowerCase()
          return fullName.includes(filter.value.toLowerCase())
        case 'title':
          return teacher.title === filter.value
        default:
          return true
      }
    })

    return searchMatches && filtersMatch
  })

  const teachersPerPage = 5
  const totalPages = Math.ceil(filteredTeachers.length / teachersPerPage)
  const indexOfLastTeacher = currentPage * teachersPerPage
  const indexOfFirstTeacher = indexOfLastTeacher - teachersPerPage
  const currentTeachers = filteredTeachers.slice(indexOfFirstTeacher, indexOfLastTeacher)

  // Adjust current page if it exceeds total pages after deletion
  // This hook must be called before any early returns
  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(totalPages)
    } else if (totalPages === 0) {
      setCurrentPage(1)
    }
  }, [totalPages, currentPage])

  const handleCreateTeacher = async (data: TeacherFormValues) => {
    try {
      if (!data.password) {
        throw new Error(t('teacher-folder.teacherForm.validation.password.required'))
      }
      const createData: CreateTeacherDTO = {
        firstname: data.firstname,
        lastname: data.lastname,
        email: data.email,
        password: data.password,
        cin: data.cin,
        title: data.title,
        birthday: data.birthday?.toISOString() || new Date().toISOString(),
        gender: data.gender,
        avatar: data.avatar || DEFAULT_AVATAR,
        isActive: data.isActive,
        subjectIds: Array.isArray(data.subjectIds) && data.subjectIds.length > 0
          ? data.subjectIds
          : [],
        nationality: data.nationality,
        address: data.address,
        phone: data.phone,
      }
      await createTeacherMutation.mutateAsync(createData)
    } catch (error) {
      // console.error('Failed to create teacher:', error)
    }
  }

  const handleUpdateTeacher = async (data: TeacherFormValues) => {
    if (!selectedTeacher?.id) return
    try {
      const updateData: UpdateTeacherDTO = {
        firstname: data.firstname,
        lastname: data.lastname,
        email: data.email,
        title: data.title,
        avatar: data.avatar || selectedTeacher.avatar,  
        isActive: data.isActive,
        subjectIds: Array.isArray(data.subjectIds) && data.subjectIds.length > 0
          ? data.subjectIds
          : [],
        cin: data.cin,
        ...(data.password && data.password.trim() !== '' ? { password: data.password } : {}),
        gender: (data.gender || 'male').toUpperCase() as 'male' | 'female',
        address: data.address,
        phone: data.phone,
        nationality: data.nationality,
        ...(data.birthday ? { birthday: data.birthday.toISOString() } : {}),
      }

      await updateTeacherMutation.mutateAsync({
        id: selectedTeacher.id,
        data: updateData,
      })
    } catch (error) {
      // console.error('Failed to update teacher:', error)
      toast({ 
        title: t('teacher-folder.teacherManagement.notifications.error.general.title'),
        description: t('teacher-folder.teacherManagement.notifications.error.general.description'),
        variant: "destructive" 
      })
    }
  }

  const uniqueTitles = [...new Set(validTeachers?.map((t) => t.title) || [])]

  const handleFilterChange = (
    key: keyof TeacherFilters,
    value: string,
    label: string
  ) => {
    setActiveFilters((prev: ActiveFilter[]) => {
      const filtered = prev.filter((f: ActiveFilter) => f.key !== key)
      if (value !== 'all') {
        return [...filtered, { key, value, label }]
      }
      return filtered
    })
    setCurrentPage(1)
  }

  const clearAllFilters = () => {
    setActiveFilters([])
    setSearchQuery('')
    setCurrentPage(1)
  }

  const handleShowProfile = (teacher: Teacher) => {
    setSelectedTeacher(teacher)
    setIsProfileModalOpen(true)
  }

  const handleDeleteClick = (teacher: Teacher) => {
    setTeacherToDelete(teacher)
    setIsDeleteDialogOpen(true)
  }

  const confirmDelete = () => {
    if (teacherToDelete) {
      deleteTeacherMutation.mutate(teacherToDelete.id)
    }
  }

  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber)
  }

  // Early returns must come after all hooks
  if (isLoading || isLoadingSubjects || showLoadingForEmpty) return <Loading />
  if (isError) return <div>Error loading data: {error?.message}</div>

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">{t('teacher-folder.teacherManagement.title')}</h1>
        <div className="flex items-center gap-2">
          <BulkTeacherImport 
            subjects={subjects.map((s) => ({
              id: String(s.id),
              name: s.name,
            }))}
            onImportComplete={() => {
              queryClient.invalidateQueries({ queryKey: ['teachers'] })
            }}
          />
          <Button 
            variant="default"
            onClick={() => setIsAddTeacherOpen(true)}
          >
            {t('teacher-folder.teacherManagement.buttons.addTeacher')}
          </Button>
          <Badge variant="secondary" className="text-sm">
            {t(validTeachers.length === 1 
              ? 'teacher-folder.teacherManagement.badges.teacherCount' 
              : 'teacher-folder.teacherManagement.badges.teacherCount_plural', 
              { count: validTeachers.length })}
          </Badge>
        </div>
      </div>

      <div className="flex items-center justify-between gap-4">
        <div className="flex-1">
          <Input
            placeholder={t('teacher-folder.teacherManagement.search.placeholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full"
          />
        </div>

        <div className="flex items-center gap-2">
          <Popover open={showFilters} onOpenChange={setShowFilters}>
            <PopoverTrigger asChild>
              <Button variant="outline" className="gap-2">
                <Filter className="h-4 w-4" />
                {t('teacher-folder.teacherManagement.filter.button')}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-80">
              <div className="space-y-4">
                <h4 className="font-medium">{t('teacher-folder.teacherManagement.filter.title')}</h4>
                
                <div className="space-y-2">
                  <Label htmlFor="name-filter">
                    {t('teacher-folder.teacherManagement.filter.fields.name.label')}
                  </Label>
                  <Input
                    id="name-filter"
                    placeholder={t('teacher-folder.teacherManagement.filter.fields.name.placeholder')}
                    onChange={(e) => handleFilterChange('name', e.target.value, `${t('teacher-folder.teacherManagement.filter.fields.name.label')}: ${e.target.value}`)}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="title-filter">
                    {t('teacher-folder.teacherManagement.filter.fields.title.label')}
                  </Label>
                  <Select
                    onValueChange={(value) =>
                      handleFilterChange('title', value, `${t('teacher-folder.teacherManagement.filter.fields.title.label')}: ${value}`)
                    }
                    defaultValue="all"
                  >
                    <SelectTrigger id="title-filter">
                      <SelectValue placeholder={t('teacher-folder.teacherManagement.filter.fields.title.label')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">
                        {t('teacher-folder.teacherManagement.filter.fields.title.options.all')}
                      </SelectItem>
                      {uniqueTitles.map((title) => (
                        <SelectItem key={title} value={title || ''}>
                          {title || t('teacher-folder.teacherManagement.filter.fields.title.options.noTitle')}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </PopoverContent>
          </Popover>
        </div>
      </div>

      {activeFilters.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-4">
          {activeFilters.map((filter) => (
            <Badge
              key={filter.key}
              variant="secondary"
              className="px-3 py-1 gap-2"
            >
              {filter.label}
              <button
                onClick={() =>
                  setActiveFilters((prev) =>
                    prev.filter((f) => f.key !== filter.key)
                  )
                }
                className="text-xs rounded-full hover:bg-gray-200 h-4 w-4 inline-flex items-center justify-center"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
          <Button
            variant="ghost"
            size="sm"
            onClick={clearAllFilters}
            className="h-7"
          >
            {t('teacher-folder.teacherManagement.filter.clearAll')}
          </Button>
        </div>
      )}

      <div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('teacher-folder.teacherManagement.table.headers.teacher')}</TableHead>
              <TableHead>{t('teacher-folder.teacherManagement.table.headers.cin')}</TableHead>
              <TableHead>{t('teacher-folder.teacherManagement.table.headers.gender')}</TableHead>
              <TableHead>{t('teacher-folder.teacherManagement.table.headers.subjects')}</TableHead>
              <TableHead>{t('teacher-folder.teacherManagement.table.headers.actions')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {currentTeachers.map((teacher) => (
              <TableRow key={teacher.id}>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full overflow-hidden">
                      <CdnImage
                        src={teacher.avatar || DEFAULT_AVATAR}
                        alt={`${teacher.firstname} ${teacher.lastname}`}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <span>
                      {teacher.firstname} {teacher.lastname}
                    </span>
                  </div>
                </TableCell>
                <TableCell>{teacher.cin}</TableCell>
                <TableCell>{teacher.gender || t('teacher-folder.teacherManagement.table.noData.gender')}</TableCell>
                <TableCell>
                  {teacher.subjects?.length || 0} {t('teacher-folder.teacherManagement.table.headers.subjects')}
                </TableCell>
                <TableCell>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={() => handleShowProfile(teacher)}>
                      {t('teacher-folder.teacherManagement.buttons.profile')}
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => handleDeleteClick(teacher)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <div className="flex justify-center items-center gap-4 mt-4">
        <Button
          variant="outline"
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="flex items-center gap-2"
        >
          <ChevronLeft className="h-4 w-4" />
          {t('teacher-folder.teacherManagement.pagination.previous')}
        </Button>
        <span className="text-sm text-gray-600">
          {t('teacher-folder.teacherManagement.pagination.pageInfo', {
            current: currentPage,
            total: totalPages
          })}
        </span>
        <Button
          variant="outline"
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="flex items-center gap-2"
        >
          {t('teacher-folder.teacherManagement.pagination.next')}
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>

      <Dialog open={isProfileModalOpen} onOpenChange={setIsProfileModalOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('teacher-folder.teacherManagement.modals.profile.title')}</DialogTitle>
          </DialogHeader>
          {selectedTeacher && (
            <TeacherForm
              initialData={selectedTeacher}
              subjects={subjects.map((s) => ({
                id: String(s.id),
                name: s.name,
              }))}
              onSubmit={handleUpdateTeacher}
              onCancel={() => setIsProfileModalOpen(false)}
              submitLabel={t('teacher-folder.teacherManagement.buttons.updateTeacher')}
              onRefreshSubjects={refetchSubjects}
            />
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('teacher-folder.teacherManagement.modals.delete.title')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t('teacher-folder.teacherManagement.modals.delete.description', {
                name: teacherToDelete 
                  ? `${teacherToDelete.firstname} ${teacherToDelete.lastname}`
                  : ''
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>
              {t('teacher-folder.teacherManagement.modals.delete.buttons.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleteTeacherMutation.isPending}
            >
              {deleteTeacherMutation.isPending 
                ? t('teacher-folder.teacherManagement.modals.delete.buttons.deleting')
                : t('teacher-folder.teacherManagement.modals.delete.buttons.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={isAddTeacherOpen} onOpenChange={setIsAddTeacherOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('teacher-folder.teacherManagement.modals.add.title')}</DialogTitle>
          </DialogHeader>
          <TeacherForm
            subjects={subjects.map((s) => ({ id: String(s.id), name: s.name }))}
            onSubmit={handleCreateTeacher}
            onCancel={() => setIsAddTeacherOpen(false)}
            submitLabel={t('teacher-folder.teacherManagement.buttons.addTeacher')}
            onRefreshSubjects={refetchSubjects}
          />
        </DialogContent>
      </Dialog>
    </div>
  )
}